/**
 * The legal entity behind EyeCaptain, in one place.
 *
 * Every legal document, the footer and the consent dialog read from here, so
 * a change of address or a new registration number is one edit. Values in
 * square brackets are still missing and must be filled before publishing;
 * `MISSING_COMPANY_DETAILS` lists them so a test or a reviewer can see what is
 * left.
 */

export const COMPANY = {
  /** Exact name as registered at Companies House. */
  legalName: "Anvilfy Ltd",
  tradingName: "EyeCaptain",
  jurisdiction: "England and Wales",
  companyNumber: "17352045",
  registeredOffice: "71-75 Shelton Street, Covent Garden, London, WC2H 9JQ, United Kingdom",
  /** Not registered for VAT. When that changes, set the number here and the terms pick it up. */
  vatNumber: null as string | null,
  /** ICO data protection fee registration (https://ico.org.uk/registration). Null until paid. */
  icoRegistration: null as string | null,
  /** Article 27 EU GDPR representative ("Name, address"). Null until appointed. */
  euRepresentative: null as string | null,
  website: "https://eyecaptain.io",
  // One inbox (hello@); support@ and sales@ are aliases of it. The roles stay separate so they can be split later.
  emails: {
    general: "hello@eyecaptain.io",
    support: "support@eyecaptain.io",
    sales: "sales@eyecaptain.io",
    privacy: "hello@eyecaptain.io",
    legal: "hello@eyecaptain.io",
    billing: "hello@eyecaptain.io",
  },
};

const EFFECTIVE = "1 October 2026";

/** Bump when a document changes materially; shown on every page. */
export const LEGAL_VERSION = {
  // 2026-10.2: payments sold through Link (Stripe Managed Payments); pay as you go credits valid 12 months.
  // 2026-10.3: 7-day free trial on paid plans (card required, one per account, trial credits).
  // 2026-10.4: the free trial is 3 days.
  // 2026-10.5: business terms rebuilt to hold (liability as separate terms, £500/£100 limit with a £5,000 fallback,
  // indemnity for claims by an agency's own clients, 14 days' notice for business customers). Applies to accounts
  // created from 7 October 2026, and to older accounts from 7 November 2026 after the notice email.
  // 2026-10.5 also covers the features released in stages (new section "Features that read your data or act on your website").
  // 2026-10.6: clarifies Managed Payments, existing direct subscriptions and custom business purchases.
  terms: { version: "2026-10.6", effective: "9 October 2026" },
  // privacy 2026-10.3: Google user data (scopes, Limited Use, no AI training), PostHog/Mixpanel/Clarity, the tracking code for
  // Live heatmaps and A/B tests, and the staged features (AI agent, mockups, Figma, Funnel, User Agent).
  privacy: { version: "2026-10.3", effective: "7 October 2026" },
  cookies: { version: "2026-10", effective: EFFECTIVE },
  // dpa 2026-10.1: liability sits under the single limit of the Terms; each party bears its own fines.
  dpa: { version: "2026-10.1", effective: "7 October 2026" },
  subprocessors: { version: "2026-10.1", effective: "7 October 2026" },
} as const;

/** Placeholders still in square brackets. The legal pages show a draft banner while this is non-empty. */
export const MISSING_COMPANY_DETAILS = Object.entries(COMPANY)
  .filter(([, v]) => typeof v === "string" && v.startsWith("["))
  .map(([k]) => k);

/** Registrations that are required but not yet in place. Not shown publicly; listed for the owner. */
export const PENDING_REGISTRATIONS = (["icoRegistration", "euRepresentative"] as const).filter((k) => !COMPANY[k]);

export const VAT_STATEMENT = COMPANY.vatNumber
  ? `VAT number: ${COMPANY.vatNumber}.`
  : "We are not registered for VAT.";

/** One line for footers and document headers. */
export function companyLine() {
  return `${COMPANY.tradingName} is operated by ${COMPANY.legalName}, a company registered in ${COMPANY.jurisdiction} (company number ${COMPANY.companyNumber}), registered office ${COMPANY.registeredOffice}.`;
}
